import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import "react-phone-input-2/lib/style.css";
// css
import styles from "./addForm.module.scss";
import { FieldArray, Formik } from "formik";
import {
  useCreateTherapistMutation,
  useGetTherapistDataQuery,
  useUpdateTherapistMutation,
} from "store/services/therapistProfiles";
import { useNavigate, useParams } from "react-router-dom";
import { CLIENT_TYPE_OPTIONS, STAFF_CLIENT_TYPE_OPTIONS } from "config/constants";
import { useDisciplineLookupQuery } from "store/services/disciplines";
import { cloneDeep, debounce, isEmpty } from "lodash";
import { successMessageToast, userFullName } from "helpers/common";
import { MANAGE_SUPPORT_STAFF_LIST, THERAPIST_LIST } from "config/paths";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { useDesignationLookupQuery } from "store/services/designations";
import { Breadcrumbs, CommonMultiSelect, CommonSelect } from "components/Common/Common";
import { genericFormErrorMsg } from "utils/validation/validationHelpers";
import AlertPop from "components/Modals/AlertPop";
import { useLazyGetFileUrlQuery } from "store/services/utility";
import LoadingOverlay from "components/shared/LoadingOverlay";
import TextInput from "components/shared/FormComponents/V2/commonV2/TextInput";
import addStaffSchemaV2 from "./addStaff.schemaV2"
import { useSchoolDivisonLookupQuery } from "store/services/schoolsDivisons";
import CommonButton from "components/shared/FormComponents/V2/commonV2/CommonButton";
import { CrossIcon } from "Assets/icons/V2";
import { useCreateSupportStaffMutation, useCreateSupportStaffV2Mutation, useGetSupportStaffDetailQuery, useUpdateSupportStaffMutation, useUpdateSupportStaffV2Mutation } from "store/services/supportStaff";
import { AppRadioField } from "components/shared/FormComponents/AppRadioField";
import { useGetHomeEducationListQuery } from "store/services/clients/homeEducation.client";

const AddSchoolStaffV2 = () => {
  const { id: staffId } = useParams();
  const editMode = !!staffId;
  const navigate = useNavigate();



  const [getFileUrl, { isLoading: getFileLoading, isFetching: getFileFetching }] = useLazyGetFileUrlQuery();

  const [updateSupportStaffV2, { isLoading: updateStaffLoading }] = useUpdateSupportStaffV2Mutation();
  const [createSupportStaffV2, { isLoading: createStaffLoading }] = useCreateSupportStaffV2Mutation();
  const { data: schoolDivisionsList, isLoading: schoolDivisionsListLoading } = useSchoolDivisonLookupQuery();

  const [isTherapistActive, setTherapistActive] = useState(false);
  const [allowDocUpload, setAllowDocUpload] = useState(true);

  const [showAlertPop, setShowAlertPop] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [clientSearch, setClientSearch] = useState(null);

  const { data, isFetching, isLoading, isError: staffError } = useGetSupportStaffDetailQuery(staffId, { skip: !staffId });
  const staffDetails = data?.record;
  const { data: homeEducationList, isLoading: homeEducationListLoading, isError: homeEducationListError, isFetching: homeEducationListFetching } = useGetHomeEducationListQuery({ page: 1, limit: 50, search: clientSearch, status: { value: "active" } });

  const homeEducationOptions = useMemo(() => {
    if (homeEducationList?.records?.length) {
      return homeEducationList.records.map(item => ({
        value: item.client_detail?.id,
        label: userFullName(item)
      }))
    }
    return [];
  }, [homeEducationList])

  const initialValues = useMemo(() => {
    if (editMode && !staffDetails) {
      return defaultInitialValues;
    }

    return ({
      ...defaultInitialValues,
      firstname: staffDetails?.firstname || "",
      lastname: staffDetails?.lastname || "",
      phone: staffDetails?.phone || "",
      email: staffDetails?.email || "",
      client_type: staffDetails?.staffDetail?.client_type || "school",
      assign_schools:
        staffDetails?.school_support_staff?.map((item) => ({
          division: item.divisions
            ? { value: item.divisions.id, label: item.divisions.title }
            : null,

          name: item.schools
            ? { value: item.schools.id, label: item.schools.name }
            : null,
        })) || [{ division: null, name: null }],
      assign_clients: staffDetails?.client_support_staff?.length ? staffDetails?.client_support_staff?.map((item) => ({
        value: item.client_id,
        label: userFullName(item.client?.client_detail)
      })) : []
    })
  }, [staffDetails, isTherapistActive, allowDocUpload])

  const handleSearchChange = useCallback(debounce((value, key = "") => {
    setClientSearch(value);
  }, 500), [setClientSearch, 500]);

  const handleClickCancel = () => {
    navigate(MANAGE_SUPPORT_STAFF_LIST);
  };

  const handleSubmit = async (payload) => {
    try {
      let values = cloneDeep(payload);
      if (values?.client_type == "school") {
        values.assign_schools = payload.assign_schools.map((item) => ({
          "school_division_id": item.division.value,
          "school_id": item.name.value
        }))
        values.assign_clients = []
      } else {
        if(!payload?.assign_clients?.length > 0 && !payload?.assign_clients?.[0]) {
          return
        }
        values.assign_clients = payload.assign_clients.map((item) => item?.value)
        values.assign_schools = []
      }
      const result = editMode ? await updateSupportStaffV2({ data: values, staffId }) : await createSupportStaffV2(values);
      if (result?.error) {

        console.error("Error create support staff", result.error);
        return;
      };

      successMessageToast(result?.data?.message);
      navigate(MANAGE_SUPPORT_STAFF_LIST);

    } catch (err) { console.error("Error create support staff", err); }
  };

  const breadcrumbItems = [
    { label: "User", path: '/manage-support-staff' },
    { label: editMode ? "Edit Support Staff" : "Add Support Staff" },
  ];

  return (
    <>
      <AlertPop
        confirmation={showAlertPop}
        setConfirmation={setShowAlertPop}
        message={alertMessage}
      />
      {
        getFileFetching && <LoadingOverlay />
      }
      <section className={`${styles.addForm} position-relative py-0 addForm`}>
        <Container fluid={true}>
          <Row
            style={{
              width: '100%',
              maxWidth: window.innerWidth >= 992 ? 'calc(100% - 170px)' : '100%',
              padding: '10px'
            }}
          >
            <Col lg={12} className="mt-2 mb-3">
              <div className="pageHead d-flex align-items-center justify-content-between flex-wrap">
                <Breadcrumbs themeBlueV2 items={breadcrumbItems} />
              </div>
              <div>
                <h3 className="h5 mt-3">{editMode ? "Edit Support Staff" : "Add Support Staff"}</h3>
              </div>
            </Col>

            <Row className="mt-0 g-3">
              {isLoading ? (
                <LoadingIndicator />
              ) : staffError ? (
                <ErrorComponent />
              ) : (
                <Formik
                  initialValues={initialValues}
                  validationSchema={addStaffSchemaV2}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({
                    handleSubmit,
                    isSubmitting,
                    errors,
                    submitCount,
                    values,
                    validateForm,
                    setFieldValue
                  }) => (
                    <Form onSubmit={handleSubmit} className="mt-0">
                      <div>
                        <h3
                          className="h5 mb-0 text-uppercase fw-medium"
                          style={{ color: "#315d99", fontSize: "14px" }}
                        >
                          1. SUPPORT STAFF INFORMATION*
                        </h3>
                        <div
                          className="py-2"
                          style={{ borderTop: "1px solid #315D99" }}
                        >

                          <Row className="g-3 mt-0">
                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="firstname"
                                label="First Name"
                                placeholder=""
                                required
                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="lastname"
                                label="Last Name"
                                required

                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="email"
                                label="Email"
                                required

                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <TextInput
                                name="phone"
                                label="Phone Number"
                              />
                            </Col>

                            <Col lg={3} md={6} className="mt-0">
                              <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                Client Type <span> *</span>
                              </label>
                              <AppRadioField
                                name="client_type"
                                label="Client Type"
                                options={STAFF_CLIENT_TYPE_OPTIONS}
                              />
                            </Col>
                          </Row>


                        </div>
                      </div>



                      <div className="mt-4">
                        <h3 className="h5 mb-0 text-uppercase fw-medium" style={{ color: "#315d99", fontSize: "14px" }}>
                          2.   ASSIGN {values?.client_type == "school" ? "SCHOOLS" : "CLIENTS"}
                        </h3>
                        {
                          values?.client_type == "school" ?
                            <div style={{ borderTop: "1px solid #315D99" }}>
                              <FieldArray name="assign_schools" >
                                {({ push, remove }) => (
                                  <>
                                    {values.assign_schools?.map((_, index) => (
                                      <Row className="g-3 align-items-end reports-v2 my-1  classroom-conslutation" key={index}>
                                        <div className="d-flex gap-3 align-items-end">
                                          <div className="w-100">
                                            <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                              School Division
                                            </label>
                                            <CommonSelect
                                              placeholder=""
                                              value={values.assign_schools[index].division}
                                              option={
                                                schoolDivisionsList?.length && schoolDivisionsList?.map(item => ({
                                                  value: item.id,
                                                  label: item.title,
                                                }))
                                              }
                                              onChange={(val) => setFieldValue(`assign_schools[${index}].division`, val)}
                                              isClearable
                                              isFilterV2

                                            />
                                          </div>
                                          <div className="w-100">
                                            <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                              School Name
                                            </label>
                                            <CommonSelect
                                              placeholder=""
                                              value={values.assign_schools[index].name}
                                              onChange={(val) => setFieldValue(`assign_schools[${index}].name`, val)}
                                              isClearable
                                              isFilterV2
                                              option={(() => {
                                                const divisionId = values.assign_schools[index].division?.value;

                                                if (!divisionId) return [];

                                                const selectedSchoolIds = values?.assign_schools?.filter(
                                                  (item) => item?.division?.value === divisionId
                                                ).map((item) => item?.name?.value)

                                                const divisionObj = schoolDivisionsList?.find(d => d.id === divisionId);

                                                if (!divisionObj?.schools?.length) return [];

                                                return cloneDeep(divisionObj.schools)
                                                  .filter(
                                                    (school) =>
                                                      !selectedSchoolIds.includes(school.id)
                                                  )
                                                  .sort((a, b) => a.name.localeCompare(b.name))
                                                  .map((school) => ({
                                                    value: school.id,
                                                    label: school.name,
                                                  }));
                                              })()}
                                            />
                                          </div>

                                          <div style={{ width: "24px" }}>
                                            {index > 0 ? (
                                              <Button
                                                onClick={() => remove(index)}
                                                variant="link"
                                                className="p-0 border-0 mb-2"
                                              >
                                                <CrossIcon classes="text-danger" />
                                              </Button>
                                            ) : null}
                                          </div>

                                        </div>
                                      </Row>
                                    ))}

                                    <CommonButton
                                      className="mt-4"
                                      text="Add Another"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        push({ division: null, name: null })
                                      }} />

                                  </>
                                )}
                              </FieldArray>
                            </div> :
                            <>
                              <div style={{ borderTop: "1px solid #315D99" }}>
                                
                                        <Row className="g-3 align-items-end reports-v2 my-1  classroom-conslutation">
                                          <div className="d-flex gap-3 align-items-end">
                                            <div style={{ width: "60%" }}>
                                              <label className="mb-1 fw-semibold" style={{ fontSize: "12px" }}>
                                                Client Name
                                              </label>
                                              <CommonMultiSelect
                                                placeholder=""
                                                // value={client}
                                                // onChange={(val) => setFieldValue(`assign_clients[${index}]`, val)}
                                                name="assign_clients"
                                                isClearable
                                                isFilterV2
                                                onInputChange={(e) => handleSearchChange(e)}
                                                isLoading={homeEducationListFetching}
                                                option={homeEducationOptions}
                                              />
                                            </div>

                                           

                                          </div>
                                        </Row>
                                  
                               

                               
                              </div>
                            </>
                        }
                      </div>

                      {!isEmpty(errors) && submitCount > 0 && (
                        <Row className="mt-4">
                          <Col lg="12">
                            <span className="error">{genericFormErrorMsg}</span>
                          </Col>
                        </Row>
                      )}

                      <Row style={{ marginTop: "55px" }}>
                        <Col lg="12">
                          <div
                            className={`${styles.btnWrpper} d-flex align-items-center justify-content-between gap-10 pt-3`}

                          >
                            <CommonButton text="Cancel" onClick={handleClickCancel} />
                            <CommonButton
                              type="submit"
                              text="Done"
                              isLoading={isSubmitting}
                              style={{ color: "#fff", background: "#315D99" }}
                              onClick={() => {
                                validateForm().then((validationErrors) => {
                                  const fieldOrder = [
                                    "firstName",
                                    "lastName",
                                    "email",
                                    "phone",
                                    "assign_schools",
                                    "assign_clients",
                                    "client_type"

                                  ];

                                  const errorKey = Object.keys(validationErrors || {})
                                    .sort(
                                      (a, b) =>
                                        fieldOrder.indexOf(a) -
                                        fieldOrder.indexOf(b)
                                    )[0];

                                  if (errorKey) {
                                    const el = document.querySelector(
                                      `[name="${errorKey}"]`
                                    );
                                    el?.scrollIntoView({
                                      behavior: "smooth",
                                      block: "center",
                                    });
                                    el?.focus();
                                  }
                                });
                              }}
                            />
                          </div>
                        </Col>
                      </Row>

                      {console.log("Formik Values:", values)}
                    </Form>
                  )}
                </Formik>
              )}
            </Row>
          </Row>
        </Container>
      </section>
    </>
  );
};

const defaultInitialValues = {
  firstname: "",
  lastname: "",
  phone: "",
  email: "",
  isLoginAllowed: "active",
  status: "active",
  assign_schools: [],
  client_type: "school",
  assign_clients: []
};

export default AddSchoolStaffV2;
